import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Circle,
  Icon,
  Divider,
  Collapse,
  Input as ChakraInput,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiMail,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGoLiveAgendaMutation,
  useSkipRevealMutation,
} from "@store/api/agenda";
import { useStartWebMeetingMutation } from "@store/api/meeting";
import { useRemixActivityMutation } from "@store/api/activities";
import {
  useToastNotification,
  useForm,
  useAuthActions,
  useStartDemo,
  useStartGuestDemo,
} from "@hooks";
import useExportAgendaPptx from "@hooks/useExportAgendaPptx";
import { isWebUser } from "@utils/helpers";
import { getActivityBgColor } from "@utils/theme/colors";
import { ActivityTypeIcon } from "@components/Activity";
import { Input } from "@components/common";
import { signupSchema } from "@schema";
import RemixActivityModal from "@components/Activity/RemixActivityModal";
import RemixLimitModal from "@components/Modals/RemixLimitModal";
import AuthGoogleBtn from "@pages/auth/GoogleAuth";

// Style overrides for auth inputs on white modal background
const modalInputStyle = {
  bg: "white",
  color: "#433358",
  borderWidth: "1px",
  borderColor: "#D0CCD5",
  borderRadius: "12px",
  _focus: {
    borderWidth: "1px",
    borderColor: "purple.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
  },
  _invalid: {
    borderWidth: "1px",
    borderColor: "red.300",
  },
};

/**
 * trackGuestCTA - Fire a Clarity custom event + tag for the CTA the guest clicked.
 * This lets us filter Clarity sessions by which action attracted the user most.
 */
const trackGuestCTA = (actionName) => {
  try {
    window.clarity?.("event", "guest_cta_click");
    window.clarity?.("set", "guest_cta", actionName);
  } catch {
    // Clarity may not be loaded in dev
  }
};

const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * GuestEmailAgendaCapture - "Email me this agenda" CTA for guests. Collapsed to a
 * single button until clicked, then expands to an inline email field, and finally
 * becomes a confirmation once the address has been captured.
 */
const GuestEmailAgendaCapture = ({ onSubmitEmailAddress }) => {
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");

  const trimmedEmailAddress = emailAddress.trim();
  const isEmailAddressValid = EMAIL_ADDRESS_PATTERN.test(trimmedEmailAddress);

  const handleSubmit = async () => {
    if (!isEmailAddressValid || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitErrorMessage("");
    try {
      await onSubmitEmailAddress(trimmedEmailAddress);
      setHasBeenSubmitted(true);
    } catch (error) {
      console.error("Failed to email agenda to guest:", error);
      setSubmitErrorMessage("We couldn't send it. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasBeenSubmitted) {
    return (
      <HStack
        w="full"
        spacing={2}
        justify="center"
        data-testid="email-agenda-success"
      >
        <Icon as={FiCheckCircle} color="#417900" boxSize={4} />
        <Text
          fontSize="13px"
          fontWeight={500}
          color="#417900"
          fontFamily="Geist, sans-serif"
          textAlign="center"
        >
          Your agenda is on its way to {trimmedEmailAddress}
        </Text>
      </HStack>
    );
  }

  if (!isEmailFormOpen) {
    return (
      <Button
        variant="outline"
        w="full"
        color="#4C47CC"
        borderColor="#4C47CC"
        fontWeight={700}
        fontSize="13px"
        borderRadius="8px"
        py={4}
        h="auto"
        onClick={() => setIsEmailFormOpen(true)}
        fontFamily="Geist, sans-serif"
        leftIcon={<Icon as={FiMail} />}
        _hover={{ bg: "#F5F3FF" }}
        data-testid="email-agenda-button"
      >
        Email me this agenda
      </Button>
    );
  }

  return (
    <VStack w="full" spacing={2} align="stretch">
      <HStack w="full" spacing={2}>
        <ChakraInput
          type="email"
          value={emailAddress}
          onChange={(event) => setEmailAddress(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSubmit();
          }}
          placeholder="you@company.com"
          fontSize="13px"
          fontFamily="Geist, sans-serif"
          borderRadius="8px"
          borderColor="#D0CCD5"
          _focus={{
            borderColor: "#4C47CC",
            boxShadow: "0 0 0 1px #4C47CC",
          }}
          data-testid="email-agenda-input"
        />
        <Button
          bg="#4C47CC"
          color="white"
          fontWeight={700}
          fontSize="13px"
          borderRadius="8px"
          px={5}
          onClick={handleSubmit}
          isDisabled={!isEmailAddressValid}
          isLoading={isSubmitting}
          fontFamily="Geist, sans-serif"
          _hover={{ bg: "#3D37B8" }}
          data-testid="email-agenda-submit"
        >
          Send
        </Button>
      </HStack>
      {submitErrorMessage && (
        <Text
          fontSize="12px"
          fontWeight={500}
          color="red.500"
          fontFamily="Geist, sans-serif"
        >
          {submitErrorMessage}
        </Text>
      )}
    </VStack>
  );
};

/**
 * GuestSignupExpansion - Inline registration section shown after a guest clicks a CTA.
 * Encapsulates auth hooks so they're only called when this component mounts.
 */
const GuestSignupExpansion = ({ onLeadCapture }) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const { values, hasError, setInputProps } = useForm(signupSchema);
  const { signupUserWithLocalStrategy } = useAuthActions();
  const { isLoading, signupUser } = signupUserWithLocalStrategy;

  const handleSignUp = async () => {
    if (hasError) return;
    onLeadCapture?.(values.email);
    await signupUser({ ...values, isFirstView: true });
  };

  return (
    <VStack spacing={4}>
      <Text
        fontSize="14px"
        fontWeight={600}
        color="#0C001C"
        textAlign="center"
        fontFamily="Geist, sans-serif"
      >
        Create an account to continue
      </Text>

      {/* Google OAuth Button */}
      <AuthGoogleBtn />

      {/* "or sign up with email" - link or divider */}
      {!showEmailForm ? (
        <Button
          variant="link"
          fontSize="14px"
          fontWeight={500}
          color="purple.500"
          textAlign="center"
          _hover={{ textDecoration: "underline" }}
          onClick={() => setShowEmailForm(true)}
        >
          or sign up with email
        </Button>
      ) : (
        <HStack w="full" align="center" spacing={3}>
          <Divider borderColor="#D0CCD5" />
          <Text
            fontSize="12px"
            color="#726682"
            whiteSpace="nowrap"
            fontWeight={500}
          >
            or sign up with email
          </Text>
          <Divider borderColor="#D0CCD5" />
        </HStack>
      )}

      {/* Expandable email/password registration form */}
      <Collapse in={showEmailForm} animateOpacity style={{ width: "100%" }}>
        <VStack spacing={3} w="full">
          <HStack spacing={3} w="full">
            <Input
              {...setInputProps({
                name: "firstName",
                variant: "auth",
                validateOnChange: true,
                requireValidation: true,
                inputProps: {
                  placeholder: "First name",
                  ...modalInputStyle,
                },
              })}
            />
            <Input
              {...setInputProps({
                name: "lastName",
                variant: "auth",
                validateOnChange: true,
                requireValidation: true,
                inputProps: {
                  placeholder: "Last name",
                  ...modalInputStyle,
                },
              })}
            />
          </HStack>
          <Input
            {...setInputProps({
              name: "email",
              variant: "auth",
              validateOnChange: true,
              requireValidation: true,
              inputProps: {
                placeholder: "Email address",
                ...modalInputStyle,
              },
            })}
          />
          <Input
            {...setInputProps({
              name: "password",
              variant: "auth",
              validateOnChange: true,
              requireValidation: true,
              inputProps: {
                placeholder: "Password",
                type: "password",
                ...modalInputStyle,
              },
            })}
          />
          <Button
            bg="purple.500"
            color="white"
            fontSize="13px"
            fontWeight={700}
            borderRadius="8px"
            w="full"
            py={3}
            _hover={{ bg: "purple.600" }}
            _active={{ bg: "purple.700", transform: "scale(0.98)" }}
            onClick={handleSignUp}
            isDisabled={hasError}
            isLoading={isLoading}
          >
            Sign Up
          </Button>
        </VStack>
      </Collapse>

      {/* Disclaimer */}
      <Text
        fontSize="14px"
        fontWeight={500}
        color="#726682"
        textAlign="center"
        lineHeight="1.43"
        letterSpacing="-0.02em"
        px={4}
      >
        We'll email you a copy too - no spam, just your agenda
      </Text>
    </VStack>
  );
};

/**
 * SummaryActivityRow - Expandable row showing a single activity
 * Shows activity details (poll options, quiz answers, links, content) when expanded.
 * In guest mode, Keep/Remix buttons are hidden.
 */
const SummaryActivityRow = ({
  activity,
  isExpanded,
  onToggle,
  onKeep,
  onRemix,
  isKept,
  isGuestMode,
}) => {
  const renderExpandedContent = () => {
    const type = activity?.type;
    const options = activity?.options || [];

    // Poll or Quiz - show options
    if (type === "poll" || type === "quiz") {
      const correctIndex = activity?.settings?.correctAnswerIndex;
      return (
        <VStack spacing={1} align="stretch" pt={2} pl="45px">
          {options.slice(0, 5).map((option, index) => {
            const isCorrect = type === "quiz" && index === correctIndex;
            const optionText =
              option?.option || option?.text || `Option ${index + 1}`;
            return (
              <Text
                key={option._id || index}
                fontSize="13px"
                fontWeight={isCorrect ? 600 : 400}
                color={isCorrect ? "#4C47CC" : "#433358"}
                fontFamily="Geist, sans-serif"
                textDecoration={isCorrect ? "underline" : "none"}
              >
                {optionText}
              </Text>
            );
          })}
        </VStack>
      );
    }

    // Share with link
    if (type === "share" && activity?.link) {
      return (
        <Box pt={2} pl="45px">
          <Text
            fontSize="13px"
            fontWeight={400}
            color="#4C47CC"
            fontFamily="Geist, sans-serif"
            as="a"
            href={activity.link}
            target="_blank"
            rel="noopener noreferrer"
            textDecoration="underline"
          >
            {activity.link}
          </Text>
        </Box>
      );
    }

    // Share/send without link - show content
    if (activity?.content) {
      return (
        <Box pt={2} pl="45px">
          <Text
            fontSize="13px"
            fontWeight={400}
            color="#433358"
            fontFamily="Geist, sans-serif"
            noOfLines={3}
          >
            {activity.content}
          </Text>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      bg="white"
      borderRadius="8px"
      p={3}
      border="1px solid"
      borderColor={isKept ? "#4C47CC" : "rgba(208, 204, 213, 0.3)"}
      boxShadow="0px 2px 4px rgba(12, 0, 28, 0.04), 0px 0px 6px rgba(12, 0, 28, 0.02)"
      cursor="pointer"
      onClick={onToggle}
      _hover={{ borderColor: isKept ? "#4C47CC" : "rgba(208, 204, 213, 0.6)" }}
      transition="border-color 0.2s"
    >
      <HStack spacing={3} align="center">
        <Circle
          size="33px"
          bg={getActivityBgColor(activity?.type, activity)}
          flexShrink={0}
        >
          <ActivityTypeIcon
            type={activity?.type}
            activity={activity}
            size="16px"
          />
        </Circle>
        <Text
          fontSize="12px"
          fontWeight={500}
          color="#000000"
          fontFamily="Geist, sans-serif"
          flex={1}
          noOfLines={isExpanded ? undefined : 1}
        >
          {activity?.type === "share" && !activity?.link
            ? activity?.content || activity?.title || "Untitled Activity"
            : activity?.title || "Untitled Activity"}
        </Text>
        <Icon
          as={isExpanded ? FiChevronUp : FiChevronDown}
          color="#726682"
          boxSize={4}
          flexShrink={0}
        />
      </HStack>
      {isExpanded && (
        <>
          {renderExpandedContent()}
          {/* Keep / Remix buttons - hidden in guest mode */}
          {!isGuestMode && (
            <HStack spacing={3} justify="flex-end" pt={3}>
              <Button
                variant="outline"
                size="sm"
                color="#4C47CC"
                borderColor="#4C47CC"
                fontWeight={600}
                fontSize="12px"
                borderRadius="6px"
                fontFamily="Geist, sans-serif"
                _hover={{ bg: "#F5F3FF" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onKeep?.(activity);
                }}
                data-testid={`keep-activity-${activity?._id}`}
              >
                Keep
              </Button>
              <Button
                size="sm"
                bg="#4C47CC"
                color="white"
                fontWeight={600}
                fontSize="12px"
                borderRadius="6px"
                fontFamily="Geist, sans-serif"
                _hover={{ bg: "#3D37B8" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemix?.(activity);
                }}
                data-testid={`remix-activity-${activity?._id}`}
              >
                Remix
              </Button>
            </HStack>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * AgendaRevealSummary - Post-creation summary screen showing all activities
 * Displayed immediately after Smart Agenda creation as a modal overlay.
 * Activities are expandable to show details (poll options, quiz answers, links, content).
 *
 * Supports guest mode: when isGuestMode=true, hides Keep/Remix and action buttons,
 * and shows inline signup CTA instead.
 */
const AgendaRevealSummary = ({
  agenda,
  activities: initialActivities,
  meetingType,
  onComplete,
  onClose,
  isGuestMode = false,
  onLeadCapture,
}) => {
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToastNotification();

  const [expandedActivityId, setExpandedActivityId] = useState(null);
  const [keptActivityIds, setKeptActivityIds] = useState(new Set());
  const [remixTargetActivity, setRemixTargetActivity] = useState(null);
  const [isRemixLimitModalOpen, setIsRemixLimitModalOpen] = useState(false);
  const [activities, setActivities] = useState(initialActivities || []);
  // Guest mode: tracks which CTA the user clicked (null = buttons visible, string = show signup)
  const [selectedGuestAction, setSelectedGuestAction] = useState(null);

  // Export to PowerPoint
  const { exportAgenda, isExporting } = useExportAgendaPptx();

  // Mutations
  const [goLiveAgenda, { isLoading: isGoingLive }] = useGoLiveAgendaMutation();
  const [startWebMeeting, { isLoading: isStartingWeb }] =
    useStartWebMeetingMutation();
  const [skipReveal, { isLoading: isSkipping }] = useSkipRevealMutation();
  const [remixActivity, { isLoading: isRemixing }] = useRemixActivityMutation();
  const { startDemo, isDemoLoading } = useStartDemo({
    source: "needs_meeting_popup",
  });
  const { startGuestDemo, isGuestDemoLoading } = useStartGuestDemo({
    source: "reveal_see_it_live",
  });

  const [isSavingForLater, setIsSavingForLater] = useState(false);
  const [showNeedsMeetingPopup, setShowNeedsMeetingPopup] = useState(false);
  const isStarting =
    isGoingLive || isStartingWeb || (isSkipping && !isSavingForLater);

  // Get meeting instance ID from Redux (Zoom context)
  const meetingInstanceId = useSelector(
    (state) => state.meeting?.meetingInstance?.uuid || null,
  );

  const isWeb = isWebUser();

  const agendaTitle = agenda?.title || "Your Agenda";

  // Mark an activity as "kept" (visual feedback)
  const handleKeepActivity = (activity) => {
    setKeptActivityIds((prev) => new Set([...prev, activity._id]));
    setExpandedActivityId(null);
  };

  // Open remix modal for a specific activity
  const handleRemixActivity = (activity) => {
    setRemixTargetActivity(activity);
  };

  // Handle remix completion from the modal
  const handleRemixComplete = async (remixData) => {
    if (!remixTargetActivity) return;

    try {
      const payload = {
        activityId: remixTargetActivity._id,
        body: {
          agendaId: agenda._id,
          hostRefinements: remixData.hostRefinements,
          keepActivityType: remixData.keepActivityType,
        },
      };

      const result = await remixActivity(payload).unwrap();

      const updatedActivities =
        result?.content?.activities || result?.activities || [];
      const updatedActivity = updatedActivities.find(
        (a) => a._id === remixTargetActivity._id,
      );

      if (updatedActivity) {
        setActivities((prev) =>
          prev.map((a) =>
            a._id === updatedActivity._id ? updatedActivity : a,
          ),
        );
      }

      showSuccessToast("Activity remixed successfully!");
      setRemixTargetActivity(null);
    } catch (error) {
      console.error("Failed to remix activity:", error);

      if (
        error?.status === 403 ||
        error?.data?.message?.includes("limit exceeded")
      ) {
        setRemixTargetActivity(null);
        setIsRemixLimitModalOpen(true);
        return;
      }

      showErrorToast(
        error?.data?.message || "Failed to remix activity. Please try again.",
      );
    }
  };

  // "Run Townhall!" - skip reveal, then start the agenda
  // For Zoom users not in a meeting, show popup explaining they need to be in a meeting
  const handleRunIt = async () => {
    if (!isWeb && !meetingInstanceId) {
      setShowNeedsMeetingPopup(true);
      return;
    }

    try {
      await skipReveal(agenda._id).unwrap();

      if (isWeb) {
        await startWebMeeting(agenda._id).unwrap();
      } else {
        await goLiveAgenda({
          agendaId: agenda._id,
          meetingInstanceId: meetingInstanceId,
        }).unwrap();
      }

      showSuccessToast("Agenda started successfully!");
      onComplete?.();
      onClose?.();
    } catch (error) {
      console.error("Failed to start agenda:", error);

      const errorMessage = error?.data?.message || "Failed to start agenda";

      if (
        error?.status === 403 ||
        errorMessage.includes("limit") ||
        errorMessage.includes("subscription")
      ) {
        showErrorToast(
          "Unable to start agenda. Please check your subscription.",
        );
      } else {
        showErrorToast(errorMessage);
      }
    }
  };

  // Start demo from the "needs meeting" popup
  const handleStartDemo = async () => {
    const agendaId = await startDemo();
    if (agendaId) {
      onClose?.();
    }
  };

  // "Save for Later" - skip reveal, then navigate away
  const handleSaveForLater = async () => {
    setIsSavingForLater(true);
    try {
      await skipReveal(agenda._id).unwrap();
    } catch (error) {
      console.error("Failed to skip reveal:", error);
    }
    onClose?.();
    navigate("/pro/agendas");
  };

  const handleExportPptx = () => {
    exportAgenda(agenda._id);
  };

  // Guest CTA click - track with Clarity and reveal signup
  const handleGuestCTAClick = (actionName) => {
    trackGuestCTA(actionName);
    setSelectedGuestAction(actionName);
  };

  // Single integration point for the future backend "send agenda by email" call.
  // Until that endpoint exists this only records the guest's address as a lead.
  const handleEmailAgendaToGuest = async (emailAddress) => {
    trackGuestCTA("email_agenda");
    await onLeadCapture?.(emailAddress);
  };

  return (
    <>
      {/* Backdrop overlay */}
      <Box
        position="fixed"
        inset="0"
        m="0"
        p="0"
        bg="rgba(12, 0, 28, 0.42)"
        zIndex={1000}
      />

      {/* Centered modal */}
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        w="100%"
        maxW="420px"
        bg="white"
        borderRadius="16px"
        zIndex={1001}
        maxH="85vh"
        overflowY="auto"
        boxShadow="0px 4px 20px rgba(12, 0, 28, 0.15)"
      >
        <VStack spacing={4} align="stretch" p="28px 20px 20px">
          {/* Header */}
          <VStack spacing={2} align="center">
            <Badge
              display="flex"
              alignItems="center"
              gap={2}
              bg="rgba(128, 204, 40, 0.17)"
              color="#417900"
              px={2}
              py={1}
              borderRadius="64px"
              fontSize="12px"
              fontWeight={400}
              fontFamily="Geist, sans-serif"
            >
              <Icon as={FiCheckCircle} boxSize={3.5} />
              Agenda is ready
            </Badge>
            <Text
              fontSize="20px"
              fontWeight={700}
              color="#0C001C"
              textAlign="center"
              lineHeight="1.33"
              letterSpacing="-0.04em"
              fontFamily="Geist, sans-serif"
            >
              {isGuestMode
                ? "Your Smart Agenda is Ready!"
                : "Your Smart Agenda"}
            </Text>
          </VStack>

          {/* Agenda Title - only for logged-in users */}
          {!isGuestMode && (
            <VStack spacing={1} align="stretch">
              <HStack spacing={2} align="center">
                <Text fontSize="24px">{"\u{1F4CA}"}</Text>
                <Text
                  fontSize="24px"
                  fontWeight={700}
                  color="#0C001C"
                  lineHeight="1.33"
                  letterSpacing="-0.04em"
                  fontFamily="Geist, sans-serif"
                >
                  {agendaTitle}
                </Text>
              </HStack>
              <Text
                fontSize="14px"
                fontWeight={400}
                color="#433358"
                fontFamily="Geist, sans-serif"
                letterSpacing="-0.02em"
              >
                Below is your Smart Agenda
              </Text>
            </VStack>
          )}

          {/* Scrollable activity list */}
          <VStack spacing={3} align="stretch">
            {activities.length === 0 ? (
              <Text
                fontSize="14px"
                color="#726682"
                textAlign="center"
                py={4}
                fontFamily="Geist, sans-serif"
              >
                No activities were generated. Please try again.
              </Text>
            ) : (
              activities.map((activity, index) => {
                const activityId = activity._id || `guest-${index}`;
                return (
                  <SummaryActivityRow
                    key={activityId}
                    activity={activity}
                    isExpanded={expandedActivityId === activityId}
                    isKept={keptActivityIds.has(activityId)}
                    isGuestMode={isGuestMode}
                    onToggle={() =>
                      setExpandedActivityId(
                        expandedActivityId === activityId ? null : activityId,
                      )
                    }
                    onKeep={handleKeepActivity}
                    onRemix={handleRemixActivity}
                  />
                );
              })
            )}
          </VStack>

          {/* Action section */}
          {isGuestMode ? (
            selectedGuestAction ? (
              <GuestSignupExpansion onLeadCapture={onLeadCapture} />
            ) : (
              <VStack spacing={4} pt={2}>
                <Button
                  w="full"
                  bg="#4C47CC"
                  color="white"
                  fontWeight={700}
                  fontSize="13px"
                  borderRadius="8px"
                  py={4}
                  h="auto"
                  onClick={() => startGuestDemo({ title: agenda?.title })}
                  isLoading={isGuestDemoLoading}
                  loadingText="Loading demo..."
                  fontFamily="Geist, sans-serif"
                  _hover={{ bg: "#3D37B8" }}
                  data-testid="see-it-live-button"
                >
                  See it live
                </Button>
                <GuestEmailAgendaCapture
                  onSubmitEmailAddress={handleEmailAgendaToGuest}
                />
                <Button
                  variant="ghost"
                  w="full"
                  color="#0C001C"
                  fontWeight={700}
                  fontSize="13px"
                  h="auto"
                  py={2}
                  onClick={() => handleGuestCTAClick("create_account")}
                  fontFamily="Geist, sans-serif"
                  _hover={{ bg: "transparent", textDecoration: "underline" }}
                  data-testid="create-account-button"
                >
                  Create a free account
                </Button>
              </VStack>
            )
          ) : (
            <VStack spacing={4} pt={2}>
              <Button
                w="full"
                bg="#4C47CC"
                color="white"
                variant="solid"
                borderColor="#4C47CC"
                fontWeight={700}
                fontSize="13px"
                borderRadius="8px"
                py={4}
                h="auto"
                onClick={handleRunIt}
                isLoading={isStarting}
                loadingText="Starting..."
                fontFamily="Geist, sans-serif"
                _hover={{ bg: "#3D37B8" }}
                data-testid="run-it-button"
              >
                Run Townhall!
              </Button>
              <Button
                variant="outline"
                w="full"
                color="#4C47CC"
                borderColor="#4C47CC"
                fontWeight={700}
                fontSize="13px"
                borderRadius="8px"
                py={4}
                h="auto"
                onClick={handleExportPptx}
                isLoading={isExporting}
                loadingText="Exporting..."
                isDisabled={isStarting}
                fontFamily="Geist, sans-serif"
                leftIcon={<Icon as={FiDownload} />}
                _hover={{ bg: "#F5F3FF" }}
                data-testid="export-pptx-button"
              >
                Export Agenda
              </Button>
              <Button
                variant="ghost"
                w="full"
                color="#0C001C"
                fontWeight={700}
                fontSize="13px"
                h="auto"
                py={2}
                onClick={handleSaveForLater}
                isDisabled={isStarting || isExporting}
                fontFamily="Geist, sans-serif"
                _hover={{ bg: "transparent", textDecoration: "underline" }}
                data-testid="save-for-later-button"
              >
                Save Agenda
              </Button>
            </VStack>
          )}
        </VStack>
      </Box>

      {/* "Needs Meeting" popup for Zoom users not in a meeting */}
      {showNeedsMeetingPopup && (
        <>
          <Box
            position="fixed"
            inset="0"
            bg="rgba(12, 0, 28, 0.5)"
            zIndex={1100}
            onClick={() => setShowNeedsMeetingPopup(false)}
          />
          <Box
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="100%"
            maxW="360px"
            bg="white"
            borderRadius="16px"
            zIndex={1101}
            boxShadow="0px 4px 20px rgba(12, 0, 28, 0.15)"
            p="28px 24px"
          >
            <VStack spacing={4} align="stretch">
              <Text
                fontSize="18px"
                fontWeight={700}
                color="#0C001C"
                textAlign="center"
                lineHeight="1.33"
                letterSpacing="-0.04em"
                fontFamily="Geist, sans-serif"
              >
                You need to be in a live meeting to run this agenda
              </Text>
              <Text
                fontSize="14px"
                color="#6B6B6B"
                textAlign="center"
                fontFamily="Geist, sans-serif"
              >
                Want to see how it works? Try our interactive demo to experience
                Townhall in action.
              </Text>
              <Button
                bg="#4C47CC"
                color="white"
                fontWeight={700}
                fontSize="13px"
                borderRadius="8px"
                py={4}
                h="auto"
                w="full"
                onClick={handleStartDemo}
                isLoading={isDemoLoading}
                loadingText="Starting Demo..."
                fontFamily="Geist, sans-serif"
                _hover={{ bg: "#3D37B8" }}
              >
                Try Demo
              </Button>
              <Button
                variant="ghost"
                w="full"
                color="#0C001C"
                fontWeight={700}
                fontSize="13px"
                h="auto"
                py={2}
                onClick={() => setShowNeedsMeetingPopup(false)}
                fontFamily="Geist, sans-serif"
                _hover={{ bg: "transparent", textDecoration: "underline" }}
              >
                Got it
              </Button>
            </VStack>
          </Box>
        </>
      )}

      {/* Remix Modal - only for authenticated users */}
      {!isGuestMode && (
        <RemixActivityModal
          isOpen={!!remixTargetActivity}
          agenda={agenda}
          onClose={() => setRemixTargetActivity(null)}
          onRemixComplete={handleRemixComplete}
          activity={remixTargetActivity}
          isLoading={isRemixing}
        />
      )}

      {!isGuestMode && (
        <RemixLimitModal
          isOpen={isRemixLimitModalOpen}
          onClose={() => setIsRemixLimitModalOpen(false)}
        />
      )}
    </>
  );
};

export default AgendaRevealSummary;
